import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { ProgressBar } from 'primereact/progressbar';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import Cropper from 'react-easy-crop';
import API_BASE_URL from '../config/api';
import {
  STREAMER_IG_USERNAME,
  STREAMER_HASHTAGS,
  PLATFORM_META,
  formatStreamerPlatformHandle,
  loadPlatformLogoImage,
  loadPreviewFonts,
  drawCoverPreview
} from '../utils/streamerPreview';

const SCHOOL_NAME = 'UniStreaming';
const IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const VIDEO_TYPES = ['video/mp4', 'video/quicktime'];
const MAX_VIDEO_CLIPS = 3;
/** Instagram carousel feed clips: 3–60 seconds per Meta docs */
const MAX_CAROUSEL_VIDEO_DURATION_SEC = 60;

function parseExistingItems(items) {
  if (!items?.length) return { cover: null, clips: [] };
  const cover = items[0]?.type !== 'video' ? items[0] : null;
  const clips = items.filter((item, index) => index > 0 && item.type === 'video');
  return { cover, clips };
}

function getVideoDurationSeconds(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(video.duration);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read video duration'));
    };
    video.src = url;
  });
}

const StreamerMediaUploadPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toastRef = useRef(null);
  const coverInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const submissionData = location.state?.submissionData || {};
  const existingItems = location.state?.photos || location.state?.mediaItems || [];
  const initialMedia = parseExistingItems(existingItems);

  const [coverPhoto, setCoverPhoto] = useState(initialMedia.cover);
  const [videoClips, setVideoClips] = useState(initialMedia.clips);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState(null);
  const [templateImage, setTemplateImage] = useState(null);
  const [templateConfig, setTemplateConfig] = useState(null);
  const [logoImages, setLogoImages] = useState({});
  const [fontsReady, setFontsReady] = useState(false);

  const [cropOpen, setCropOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const allMediaItems = coverPhoto ? [coverPhoto, ...videoClips] : [];

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!submissionData.name) {
      navigate('/streamer/submit');
    }
  }, [submissionData.name, navigate]);

  useEffect(() => {
    const loadTemplate = async () => {
      try {
        const [templateRes, configRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/template/streamer-university`),
          fetch(`${API_BASE_URL}/api/streamer/template-config`)
        ]);
        const templateResult = await templateRes.json();
        const configResult = await configRes.json();

        if (configResult.status === 'success') {
          setTemplateConfig(configResult.data);
        }

        if (templateResult.status === 'success' && templateResult.data.templateUrl) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => setTemplateImage(img);
          img.onerror = () => console.error('Failed to load template image');
          img.src = templateResult.data.templateUrl;
        }
      } catch (error) {
        console.error('Error loading template:', error);
      }
    };
    loadTemplate();
  }, []);

  useEffect(() => {
    loadPreviewFonts()
      .then(() => setFontsReady(true))
      .catch(() => setFontsReady(true));
  }, []);

  useEffect(() => {
    const platforms = (submissionData.platforms || [])
      .map(({ platform }) => platform)
      .filter(Boolean);

    if (!platforms.length) return;

    let cancelled = false;

    Promise.all(
      platforms.map(async (platform) => {
        const img = await loadPlatformLogoImage(platform);
        return { platform, img };
      })
    ).then((results) => {
      if (cancelled) return;
      setLogoImages((prev) => {
        const next = { ...prev };
        results.forEach(({ platform, img }) => {
          if (img) next[platform] = img;
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [submissionData.platforms]);

  useEffect(() => {
    if (coverPhoto && templateImage && submissionData.name && fontsReady) {
      generateCoverPreview();
    } else {
      setCoverPreviewUrl(null);
    }
  }, [coverPhoto, templateImage, submissionData.name, submissionData.platforms, templateConfig, logoImages, fontsReady]);

  const generateCoverPreview = () => {
    if (!coverPhoto || !templateImage || !submissionData.name) return;

    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const size = 800;
      canvas.width = size;
      canvas.height = size;

      const coverImg = new Image();
      coverImg.onload = () => {
        drawCoverPreview({
          ctx,
          size,
          coverImg,
          templateImage,
          submissionData,
          logoImages
        });
        setCoverPreviewUrl(canvas.toDataURL('image/jpeg', 0.95));
      };
      coverImg.src = coverPhoto.previewUrl;
    } catch (error) {
      console.error('Error generating cover preview:', error);
    }
  };

  const autoCropToSquare = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const size = 800;
        canvas.width = size;
        canvas.height = size;

        const minDimension = Math.min(img.width, img.height);
        const startX = (img.width - minDimension) / 2;
        const startY = (img.height - minDimension) / 2;

        ctx.drawImage(img, startX, startY, minDimension, minDimension, 0, 0, size, size);

        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Failed to create blob'));
        }, 'image/jpeg', 0.9);
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixelsValue) => {
    setCroppedAreaPixels(croppedAreaPixelsValue);
  }, []);

  const createCroppedImage = async (imageSrc, pixelCrop) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const size = 800;
        canvas.width = size;
        canvas.height = size;

        ctx.drawImage(
          image,
          pixelCrop.x,
          pixelCrop.y,
          pixelCrop.width,
          pixelCrop.height,
          0,
          0,
          size,
          size
        );

        canvas.toBlob((blob) => {
          if (blob) {
            resolve({ blob, url: URL.createObjectURL(blob) });
          } else {
            reject(new Error('Failed to create blob'));
          }
        }, 'image/jpeg', 0.9);
      };
      image.onerror = () => reject(new Error('Failed to load image'));
      image.src = imageSrc;
    });
  };

  const openCropModal = () => {
    if (!coverPhoto) return;
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setCropOpen(true);
  };

  const saveCroppedImage = async () => {
    if (!coverPhoto || !croppedAreaPixels) return;

    try {
      const imageSource = coverPhoto.originalUrl || coverPhoto.previewUrl;
      const { blob, url } = await createCroppedImage(imageSource, croppedAreaPixels);

      URL.revokeObjectURL(coverPhoto.previewUrl);

      setCoverPhoto({
        ...coverPhoto,
        blob,
        previewUrl: url,
        type: 'image'
      });
      setCropOpen(false);

      toastRef.current?.show({
        severity: 'success',
        summary: 'Photo Cropped',
        detail: 'Your cover photo has been cropped successfully.',
        life: 3000
      });
    } catch (error) {
      console.error('Error cropping image:', error);
      toastRef.current?.show({
        severity: 'error',
        summary: 'Crop Failed',
        detail: 'Failed to crop the image. Please try again.',
        life: 3000
      });
    }
  };

  const revokeMediaItem = (item) => {
    if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
    if (item?.originalUrl) URL.revokeObjectURL(item.originalUrl);
  };

  const handleCoverSelect = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!IMAGE_TYPES.includes(file.type)) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Photos Only',
        detail: 'Your cover must be a photo (JPEG, PNG, or WebP).',
        life: 4000
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'File Too Large',
        detail: `${file.name} is larger than 10MB.`,
        life: 3000
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      if (coverPhoto) revokeMediaItem(coverPhoto);

      const originalUrl = URL.createObjectURL(file);
      const croppedBlob = await autoCropToSquare(file);
      const previewUrl = URL.createObjectURL(croppedBlob);

      setCoverPhoto({
        blob: croppedBlob,
        previewUrl,
        originalUrl,
        fileName: `cover-${Date.now()}.jpg`,
        type: 'image',
        uploaded: false
      });

      toastRef.current?.show({
        severity: 'success',
        summary: 'Cover Photo Added',
        detail: 'You can continue with just your cover, or add up to 3 video clips below.',
        life: 4000
      });
    } catch (error) {
      console.error('Cover processing error:', error);
      toastRef.current?.show({
        severity: 'error',
        summary: 'Processing Failed',
        detail: 'Failed to process your cover photo. Please try again.',
        life: 3000
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleVideoSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;

    if (!coverPhoto) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'Cover Photo First',
        detail: 'Upload your cover photo before adding video clips.',
        life: 4000
      });
      return;
    }

    const remainingSlots = MAX_VIDEO_CLIPS - videoClips.length;
    if (remainingSlots <= 0) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'Clip Limit Reached',
        detail: `You can add up to ${MAX_VIDEO_CLIPS} video clips.`,
        life: 3000
      });
      return;
    }

    if (files.length > remainingSlots) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'Too Many Clips',
        detail: `You can only add ${remainingSlots} more clip${remainingSlots === 1 ? '' : 's'}.`,
        life: 3000
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const processedClips = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (!VIDEO_TYPES.includes(file.type)) {
        toastRef.current?.show({
          severity: 'error',
          summary: 'Videos Only',
          detail: `${file.name} must be MP4 or MOV.`,
          life: 3000
        });
        continue;
      }

      if (file.size > 100 * 1024 * 1024) {
        toastRef.current?.show({
          severity: 'error',
          summary: 'File Too Large',
          detail: `${file.name} is larger than 100MB.`,
          life: 3000
        });
        continue;
      }

      try {
        const duration = await getVideoDurationSeconds(file);
        if (duration > MAX_CAROUSEL_VIDEO_DURATION_SEC) {
          toastRef.current?.show({
            severity: 'error',
            summary: 'Video Too Long',
            detail: `${file.name} is ${Math.round(duration)}s. Clips must be 60 seconds or less.`,
            life: 5000
          });
          continue;
        }
      } catch {
        toastRef.current?.show({
          severity: 'error',
          summary: 'Invalid Video',
          detail: `Could not read ${file.name}. Try re-exporting as MP4.`,
          life: 4000
        });
        continue;
      }

      processedClips.push({
        blob: file,
        previewUrl: URL.createObjectURL(file),
        fileName: file.name,
        type: 'video',
        fileType: file.type,
        uploaded: false
      });

      setUploadProgress(((i + 1) / files.length) * 100);
    }

    if (processedClips.length > 0) {
      setVideoClips((prev) => [...prev, ...processedClips]);
      toastRef.current?.show({
        severity: 'success',
        summary: 'Clips Added',
        detail: `Added ${processedClips.length} clip${processedClips.length > 1 ? 's' : ''}.`,
        life: 3000
      });
    }

    setUploading(false);
    setUploadProgress(0);
  };

  const removeCoverPhoto = () => {
    if (coverPhoto) revokeMediaItem(coverPhoto);
    setCoverPhoto(null);
    videoClips.forEach(revokeMediaItem);
    setVideoClips([]);
  };

  const removeVideoClip = (index) => {
    const clip = videoClips[index];
    revokeMediaItem(clip);
    setVideoClips(videoClips.filter((_, i) => i !== index));
  };

  const clearAllMedia = () => {
    if (coverPhoto) revokeMediaItem(coverPhoto);
    videoClips.forEach(revokeMediaItem);
    setCoverPhoto(null);
    setVideoClips([]);
    toastRef.current?.show({
      severity: 'info',
      summary: 'Media Cleared',
      detail: 'All media has been removed.',
      life: 3000
    });
  };

  const handleNext = async () => {
    if (uploading) return;

    if (!coverPhoto) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'Cover Photo Required',
        detail: 'Upload a cover photo to continue. Video clips are optional.',
        life: 3000
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      setUploadProgress(10);

      let submissionId = location.state?.submissionId;

      if (!submissionId) {
        const profileResponse = await fetch(`${API_BASE_URL}/api/streamer/submissions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(submissionData)
        });

        if (!profileResponse.ok) {
          throw new Error('Failed to create submission');
        }

        const profileResult = await profileResponse.json();
        submissionId = profileResult.data.id;
      }

      setUploadProgress(30);
      const mediaKeys = [];

      for (let i = 0; i < allMediaItems.length; i++) {
        const item = allMediaItems[i];
        const isVideo = item.type === 'video';
        const fileType = isVideo ? (item.fileType || 'video/mp4') : 'image/jpeg';

        const signedUrlResponse = await fetch(`${API_BASE_URL}/api/streamer/media/upload-url`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileName: item.fileName,
            fileType,
            submissionId,
            mediaIndex: i,
            mediaType: item.type
          })
        });

        if (!signedUrlResponse.ok) {
          throw new Error(`Failed to get upload URL for item ${i + 1}`);
        }

        const { data } = await signedUrlResponse.json();
        const file = isVideo
          ? item.blob
          : new File([item.blob], item.fileName, { type: 'image/jpeg' });

        const uploadResponse = await fetch(data.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': fileType }
        });

        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload item ${i + 1}`);
        }

        mediaKeys.push({ key: data.fileKey, type: item.type });
        setUploadProgress(30 + ((i + 1) / allMediaItems.length) * 60);
      }

      setUploadProgress(90);

      const updateResponse = await fetch(`${API_BASE_URL}/api/streamer/submissions/${submissionId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mediaKeys })
      });

      if (!updateResponse.ok) {
        const errorData = await updateResponse.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || 'Failed to update submission with media');
      }

      setUploadProgress(100);

      toastRef.current?.show({
        severity: 'success',
        summary: 'Upload Complete',
        detail: 'Almost done — confirm and submit your post.',
        life: 2000
      });

      setTimeout(() => {
        navigate('/streamer/tier-selection', {
          state: { submissionId, submissionData, photos: allMediaItems }
        });
      }, 1000);
    } catch (error) {
      console.error('Submission error:', error);
      toastRef.current?.show({
        severity: 'error',
        summary: 'Upload Failed',
        detail: error.message || 'Failed to submit. Please try again.',
        life: 5000
      });
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleBack = () => {
    if (allMediaItems.length > 0) {
      confirmDialog({
        message: 'Going back will remove all your uploaded media. Are you sure you want to continue?',
        header: 'Warning',
        icon: '',
        accept: () => {
          if (coverPhoto) revokeMediaItem(coverPhoto);
          videoClips.forEach(revokeMediaItem);
          navigate('/streamer/submit', { state: { submissionData } });
        },
        reject: () => {},
        acceptClassName: 'p-button-danger',
        acceptLabel: '',
        rejectLabel: ''
      });
    } else {
      navigate('/streamer/submit', { state: { submissionData } });
    }
  };

  const progressValue = coverPhoto ? 50 + (videoClips.length / MAX_VIDEO_CLIPS) * 50 : 0;
  const progressLabel = coverPhoto
    ? videoClips.length > 0
      ? `Cover ready • ${videoClips.length}/${MAX_VIDEO_CLIPS} clips`
      : 'Cover ready • clips optional'
    : 'Upload your cover photo to continue';

  return (
    <div className="photo-upload-page">
      <Toast ref={toastRef} />
      <ConfirmDialog />

      <header className="form-header">
        <div className="header-content">
          <Button
            icon="pi pi-arrow-left"
            label="Back"
            className="back-button"
            onClick={handleBack}
          />
          <div className="header-title">
            <p>{SCHOOL_NAME}</p>
          </div>
        </div>
      </header>

      <div className="progress-section">
        <div className="container">
          <div className="progress-info">
            <span>Step 2 of 3: Cover & Clips</span>
            <span>{progressLabel}</span>
          </div>
          <ProgressBar
            value={progressValue}
            className="form-progress"
            showValue={false}
          />
        </div>
      </div>

      <main className="photo-upload-main">
        <div className="container">

          {coverPhoto && (
            <Card className="cover-preview-card">
              <div className="cover-preview-header">
                <h2>Instagram Post Preview</h2>
                <p>See exactly how your post will look on @{STREAMER_IG_USERNAME}</p>
              </div>

              <div className="instagram-post-preview">
                <div className="ig-mockup">
                  <div className="ig-mockup-header">
                    <div className="ig-avatar">
                      <span>US</span>
                    </div>
                    <span className="ig-username">{STREAMER_IG_USERNAME}</span>
                    <i className="pi pi-ellipsis-h ig-more-icon" />
                  </div>

                  <div className="post-image-container">
                    {coverPreviewUrl ? (
                      <img src={coverPreviewUrl} alt="Cover with template" className="cover-image" />
                    ) : (
                      <div className="loading-preview">
                        <img src={coverPhoto.previewUrl} alt="Cover" className="cover-image" />
                        <div className="loading-overlay">
                          <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
                          <p>Loading preview...</p>
                        </div>
                      </div>
                    )}
                    {allMediaItems.length > 1 && (
                      <div className="ig-carousel-dots">
                        {allMediaItems.map((_, i) => (
                          <span key={i} className={`ig-dot ${i === 0 ? 'active' : ''}`} />
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="ig-actions">
                    <div className="ig-actions-left">
                      <i className="pi pi-heart" />
                      <i className="pi pi-comment" />
                      <i className="pi pi-send" />
                    </div>
                    <i className="pi pi-bookmark ig-bookmark" />
                  </div>

                  <div className="ig-caption">
                    <p className="ig-caption-text">
                      <strong>{STREAMER_IG_USERNAME}</strong>{' '}
                      {submissionData.bio}
                    </p>
                    {(submissionData.platforms || []).map(({ platform, handle }) => {
                      if (!handle) return null;
                      const label = PLATFORM_META[platform]?.label || platform;
                      return (
                        <p key={platform} className="ig-platform-line">
                          {label}: {formatStreamerPlatformHandle(platform, handle)}
                        </p>
                      );
                    })}
                    <p className="ig-hashtags">{STREAMER_HASHTAGS.join(' ')}</p>
                    <p className="ig-cta">Want to be featured? Click the link in our bio!</p>
                    <p className="ig-caption-hint">Caption preview — your cover image includes your name and platform handles</p>
                  </div>
                </div>
              </div>
            </Card>
          )}

          <Card className="upload-section-card streamer-upload-steps">
            <div className={`upload-step ${coverPhoto ? 'upload-step-complete' : 'upload-step-active'}`}>
              <div className="upload-step-header">
                <span className="upload-step-number">1</span>
                <div>
                  <h2>Cover Photo</h2>
                  <p>
                    Upload <strong>one photo</strong> for your Instagram cover. This is required.
                    Photos only — no videos here.
                  </p>
                </div>
              </div>

              <div className="upload-buttons">
                <Button
                  icon="pi pi-image"
                  label={coverPhoto ? 'Replace Cover Photo' : 'Upload Cover Photo'}
                  className="upload-btn"
                  onClick={() => coverInputRef.current?.click()}
                  disabled={uploading}
                />
                {coverPhoto && (
                  <>
                    <Button
                      icon="pi pi-expand"
                      label="Crop"
                      className="p-button-outlined"
                      onClick={openCropModal}
                      disabled={uploading}
                    />
                    <Button
                      icon="pi pi-times"
                      label="Remove"
                      className="clear-btn p-button-outlined"
                      onClick={removeCoverPhoto}
                      disabled={uploading}
                    />
                  </>
                )}
              </div>

              {coverPhoto && (
                <div className="photo-grid cover-only-grid">
                  <div className="photo-item">
                    <div className="photo-wrapper">
                      <img src={coverPhoto.previewUrl} alt="Cover photo" />
                      <div className="cover-badge">Cover</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className={`upload-step ${!coverPhoto ? 'upload-step-locked' : videoClips.length >= MAX_VIDEO_CLIPS ? 'upload-step-complete' : 'upload-step-active'}`}>
              <div className="upload-step-header">
                <span className="upload-step-number">2</span>
                <div>
                  <h2>Video Clips <span className="optional-label">Optional</span></h2>
                  <p>
                    {coverPhoto
                      ? `Add up to ${MAX_VIDEO_CLIPS} short video clips (MP4/MOV, max 60 seconds each), or skip this step and post with just your cover photo.`
                      : 'Upload your cover photo first, then you can optionally add up to 3 video clips.'}
                  </p>
                </div>
              </div>

              <div className="upload-buttons">
                <Button
                  icon="pi pi-video"
                  label={videoClips.length === 0 ? 'Add Video Clips' : 'Add More Clips'}
                  className="upload-btn"
                  onClick={() => videoInputRef.current?.click()}
                  disabled={!coverPhoto || videoClips.length >= MAX_VIDEO_CLIPS || uploading}
                />
                {allMediaItems.length > 0 && (
                  <Button
                    icon="pi pi-trash"
                    label="Clear All"
                    className="clear-btn p-button-outlined"
                    onClick={clearAllMedia}
                    disabled={uploading}
                  />
                )}
              </div>

              {videoClips.length > 0 && (
                <div className="photo-grid">
                  {videoClips.map((clip, index) => (
                    <div key={index} className="photo-item">
                      <div className="photo-wrapper">
                        <video src={clip.previewUrl} muted playsInline />
                        <div className="cover-badge" style={{ top: 'auto', bottom: '8px', background: 'rgba(0,0,0,0.7)' }}>
                          Clip {index + 1}
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                        <Button
                          icon="pi pi-times"
                          className="p-button-rounded p-button-text"
                          onClick={() => removeVideoClip(index)}
                          title="Remove clip"
                          style={{ color: '#dc3545', width: '36px', height: '36px' }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {uploading && (
              <div className="upload-status">
                <p>Processing {Math.round(uploadProgress)}% complete...</p>
                <ProgressBar value={uploadProgress} showValue={false} />
              </div>
            )}

            <div className="form-actions">
              <Button
                label={uploading ? 'Uploading...' : 'Next: Submit Post'}
                icon={uploading ? 'pi pi-spin pi-spinner' : 'pi pi-arrow-right'}
                iconPos="right"
                className="next-button"
                onClick={handleNext}
                disabled={!coverPhoto || uploading}
              />
              {coverPhoto && videoClips.length === 0 && (
                <p className="next-hint">You can continue with just your cover photo.</p>
              )}
            </div>
          </Card>
        </div>
      </main>

      <input
        ref={coverInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={handleCoverSelect}
      />

      <input
        ref={videoInputRef}
        type="file"
        accept="video/mp4,video/quicktime"
        multiple
        style={{ display: 'none' }}
        onChange={handleVideoSelect}
      />

      {cropOpen && coverPhoto && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
            <Cropper
              image={coverPhoto.originalUrl || coverPhoto.previewUrl}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>

          <div
            style={{
              padding: '20px',
              backgroundColor: '#000000',
              display: 'flex',
              flexDirection: 'column',
              gap: '15px'
            }}
          >
            <div style={{ textAlign: 'center', color: '#ffffff', fontSize: '14px', fontWeight: '500' }}>
              Pinch or drag to zoom and position
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <i className="pi pi-search-minus" style={{ color: '#ffffff', fontSize: '18px' }}></i>
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                style={{
                  flex: 1,
                  height: '6px',
                  borderRadius: '3px',
                  outline: 'none',
                  background: '#4a4a4a'
                }}
              />
              <i className="pi pi-search-plus" style={{ color: '#ffffff', fontSize: '18px' }}></i>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <Button
                label="Cancel"
                className="p-button-secondary"
                onClick={() => setCropOpen(false)}
                style={{ color: '#ffffff', flex: 1, fontSize: '16px', padding: '12px' }}
              />
              <Button
                label="Save"
                className="p-button-success"
                onClick={saveCroppedImage}
                style={{ color: '#ffffff', flex: 1, fontSize: '16px', padding: '12px' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StreamerMediaUploadPage;
